import { useState, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import Tooltip from '@mui/material/Tooltip';
import { Timestamp } from 'firebase/firestore';
import type { ChecklistItem, ChecklistStatus, Milestone } from '@altyor-kronos/shared-types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../core/auth/useAuth';
import {
  createMilestone,
  reopenMilestone,
  seedProgramMilestones,
  updateMilestoneChecklist,
  updateMilestoneTargetDate,
  validateMilestone,
} from '../../core/data/projectMutations';
import { useMilestones } from './useMilestones';
import { useUsersList } from './useUsersList';
import { colors } from '../../core/theme/theme';
import MilestoneRow, { ROW_GRID, formatDay } from './MilestoneRow';
import MilestoneReviewPanel from './MilestoneReviewPanel';
import { milestoneCompliance, FV_DONE_STATUSES } from './compliance';
import type { ProjectDetailContext } from './ProjectOverview';

export default function ProjectMilestonesTab() {
  const { project } = useOutletContext<ProjectDetailContext>();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { milestones, loading, error, refresh } = useMilestones(project.id);
  const now = useMemo(() => new Date(), []);

  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<Error | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<Error | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Miroir de projectScoped.ts milestones.update : admin | direction | team.managers.
  // `quality` et `project_manager` n'existent pas dans le token Entra (modèle à
  // 4 rôles, #48) — les tester ici ne protégeait rien et promettait des droits
  // que le serveur refuse.
  const canEdit =
    (user?.roles.includes('admin') ?? false) ||
    (user?.roles.includes('direction') ?? false) ||
    (project.team?.managers?.includes(user?.uid ?? '') ?? false);

  // Rouvrir une revue franchie est plus restreint que l'éditer : le manager du
  // projet valide, la direction seule défait. Le serveur laisse passer les deux
  // (même règle `milestones.update`) — c'est un garde-fou de processus, pas de
  // sécurité, et il est assumé comme tel.
  const canReopen =
    (user?.roles.includes('admin') ?? false) || (user?.roles.includes('direction') ?? false);

  // Users list for the "who" autocomplete — fetched only when editor (permission gate).
  const { users: allUsers } = useUsersList(canEdit);
  const userOptions = useMemo(
    () => allUsers.map((u) => u.data.displayName).filter(Boolean),
    [allUsers],
  );

  /**
   * Les quatre KPI de la maquette, dérivés d'un seul passage sur les jalons via
   * `milestoneCompliance` — pastille, bandeau et vue transverse partagent donc
   * exactement le même calcul de retard.
   */
  const kpi = useMemo(() => {
    let worstLate = 0;
    let worstName = '';
    let fvDone = 0;
    let fvTotal = 0;
    let nextDue: Date | null = null;

    for (const { data: ms } of milestones) {
      const c = milestoneCompliance(ms, now);
      if (c.daysLate > worstLate) {
        worstLate = c.daysLate;
        worstName = ms.name;
      }
      for (const d of ms.deliverables ?? []) {
        fvTotal += 1;
        if (FV_DONE_STATUSES.includes(d.status)) fvDone += 1;
      }
      if (c.state === 'upcoming' && c.effectiveDate && (!nextDue || c.effectiveDate < nextDue)) {
        nextDue = c.effectiveDate;
      }
    }

    return {
      worstLate,
      worstName,
      fvDone,
      fvTotal,
      passed: milestones.filter((m) => m.data.status === 'done').length,
      total: milestones.length,
      nextDue,
    };
  }, [milestones, now]);

  const handleCreateMilestone = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    setAddError(null);
    try {
      const nextOrder = milestones.length
        ? Math.max(...milestones.map((m) => m.data.order)) + 1
        : 0;
      await createMilestone(project.id, { name, order: nextOrder });
      refresh();
      setNewName('');
      setAddOpen(false);
    } catch (err) {
      setAddError(err as Error);
    } finally {
      setAdding(false);
    }
  }, [newName, milestones, project.id, refresh]);

  const handleCreateDefaults = useCallback(async () => {
    setSeeding(true);
    setSeedError(null);
    try {
      await seedProgramMilestones(project.id, project.currentProgram);
      refresh();
    } catch (err) {
      setSeedError(err as Error);
    } finally {
      setSeeding(false);
    }
  }, [project.id, project.currentProgram, refresh]);

  // Spinner au PREMIER chargement seulement : `refresh()` repasse `loading` à
  // true après chaque mutation, et vider la liste à ce moment ferait clignoter
  // tout l'écran à chaque clic.
  if (loading && milestones.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (milestones.length === 0) {
    return (
      <Stack spacing={2} data-testid="milestones-empty">
        <Alert severity="info">
          {canEdit
            ? t('projects.milestonesTab.noMilestonesEditor')
            : t('projects.milestonesTab.noMilestonesViewer')}
        </Alert>
        {canEdit && (
          <Box>
            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={() => void handleCreateDefaults()}
              disabled={seeding}
              data-testid="milestones-create-defaults"
            >
              {t('projects.milestonesTab.createDefaults')}
            </Button>
          </Box>
        )}
        {seedError && <Alert severity="error">{seedError.message}</Alert>}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        data-testid="milestones-summary"
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, rowGap: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {t('projects.milestonesTab.title')}
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            data-testid="milestones-add-milestone"
          >
            {t('projects.milestonesTab.addMilestone')}
          </Button>
        )}
      </Box>

      {/* Bandeau KPI — carte sombre, seul emplacement d'emphase de l'écran. */}
      <Box
        data-testid="milestones-kpis"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          backgroundColor: colors.darkBg,
          color: colors.white,
        }}
      >
        <KpiCell
          testId="milestones-kpi-late"
          value={kpi.worstLate > 0 ? `+${kpi.worstLate}` : '0'}
          alert={kpi.worstLate > 14}
          label={
            kpi.worstName
              ? t('projects.milestonesTab.kpiLateOn', { name: kpi.worstName })
              : t('projects.milestonesTab.kpiLate')
          }
        />
        <KpiCell
          testId="milestones-kpi-fv"
          value={`${kpi.fvDone}/${kpi.fvTotal}`}
          label={t('projects.milestonesTab.kpiFv')}
        />
        <KpiCell
          testId="milestones-kpi-passed"
          value={`${kpi.passed}/${kpi.total}`}
          label={t('projects.milestonesTab.kpiPassed')}
        />
        <KpiCell
          testId="milestones-kpi-next"
          value={kpi.nextDue ? formatDay(kpi.nextDue).slice(0, 5) : '—'}
          label={t('projects.milestonesTab.kpiNext')}
        />
      </Box>

      {/* Liste datée */}
      <Box data-testid="milestones-tab" sx={{ border: `1px solid ${colors.lightGray}` }}>
        <Box
          sx={{
            ...ROW_GRID,
            backgroundColor: colors.lightGray,
            fontWeight: 700,
            fontSize: 11,
            borderBottom: `2px solid ${colors.bodyText}`,
          }}
        >
          <Box />
          <Box>{t('projects.milestonesTab.colMilestone')}</Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {t('projects.milestonesTab.colTarget')}
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {t('projects.milestonesTab.colRescheduled')}
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {t('projects.milestonesTab.colState')}
          </Box>
          <Box />
        </Box>

        {milestones.map(({ id, data: ms }) => (
          <MilestoneRow
            key={id}
            milestoneId={id}
            milestone={ms}
            now={now}
            expanded={expandedId === id}
            onToggle={() => setExpandedId((cur) => (cur === id ? null : id))}
            action={
              canEdit ? (
                <PlainMilestoneAction
                  projectId={project.id}
                  milestoneId={id}
                  milestone={ms}
                  onDone={refresh}
                />
              ) : undefined
            }
          >
            {ms.reviewName ? (
              <MilestoneReviewPanel
                projectId={project.id}
                milestoneId={id}
                milestone={ms}
                canEdit={canEdit}
                canReopen={canReopen}
                now={now}
                onDone={refresh}
              />
            ) : ms.checklist.length > 0 ? (
              <LegacyChecklistPanel
                projectId={project.id}
                milestoneId={id}
                milestone={ms}
                canEdit={canEdit}
                userOptions={userOptions}
                onDone={refresh}
              />
            ) : undefined}
          </MilestoneRow>
        ))}
      </Box>

      {/* New-milestone dialog */}
      <Dialog
        open={addOpen}
        onClose={() => {
          if (!adding) {
            setAddOpen(false);
            setNewName('');
            setAddError(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('projects.milestonesTab.addMilestoneTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={t('projects.milestonesTab.milestoneName')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim() && !adding) {
                e.preventDefault();
                void handleCreateMilestone();
              }
            }}
            disabled={adding}
            data-testid="milestones-add-milestone-input"
          />
          {addError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {addError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddOpen(false);
              setNewName('');
              setAddError(null);
            }}
            disabled={adding}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateMilestone()}
            disabled={!newName.trim() || adding}
            data-testid="milestones-add-milestone-confirm"
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ─── KPI cell ───────────────────────────────────────────────────

function KpiCell({
  testId,
  value,
  label,
  alert,
}: {
  testId: string;
  value: string;
  label: string;
  alert?: boolean;
}) {
  return (
    <Box
      data-testid={testId}
      sx={{
        px: 2.25,
        py: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        borderRight: `1px solid #262A32`,
        '&:last-of-type': { borderRight: 0 },
      }}
    >
      <Typography
        sx={{
          fontSize: 32,
          fontWeight: 900,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          color: alert ? colors.red : 'inherit',
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#9AA0A8' }}>{label}</Typography>
    </Box>
  );
}

// ─── Franchir / rouvrir un jalon sans revue ─────────────────────

function PlainMilestoneAction({
  projectId,
  milestoneId,
  milestone,
  onDone,
}: {
  projectId: string;
  milestoneId: string;
  milestone: Milestone;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const isDone = milestone.status === 'done';
  const label = isDone
    ? t('projects.milestonesTab.reopen')
    : t('projects.milestonesTab.validate');

  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        aria-label={label}
        data-testid={`milestone-${isDone ? 'reopen' : 'validate'}-${milestoneId}`}
        onClick={() =>
          void (isDone
            ? reopenMilestone(projectId, milestoneId)
            : validateMilestone(projectId, milestoneId)
          ).then(onDone)
        }
      >
        {isDone ? <UndoIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

// ─── Checklist héritée (LaunchPad / UnLock d'avant le PAD) ──────

/**
 * ponytail: conservé tel quel, et seulement pour les jalons qui en portent
 * déjà une. Le modèle PAD la remplace par les fiches de validation ; on
 * n'en crée plus, mais la masquer rendrait invisible ce que des projets
 * LaunchPad ont réellement saisi.
 */
function LegacyChecklistPanel({
  projectId,
  milestoneId,
  milestone,
  canEdit,
  userOptions,
  onDone,
}: {
  projectId: string;
  milestoneId: string;
  milestone: Milestone;
  canEdit: boolean;
  userOptions: string[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const isDone = milestone.status === 'done';
  const inEditMode = canEdit && !isDone;

  const updateChecklist = useCallback(
    async (checklist: ChecklistItem[]) => {
      await updateMilestoneChecklist(projectId, milestoneId, checklist);
      onDone();
    },
    [projectId, milestoneId, onDone],
  );

  const handleAddItem = useCallback(async () => {
    await updateChecklist([...milestone.checklist, { what: '', who: '', status: 'pending' }]);
  }, [milestone.checklist, updateChecklist]);

  const handleUpdateItem = useCallback(
    async (index: number, field: keyof ChecklistItem, value: unknown) => {
      await updateChecklist(
        milestone.checklist.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
      );
    },
    [milestone.checklist, updateChecklist],
  );

  const handleDeleteItem = useCallback(
    async (index: number) => {
      await updateChecklist(milestone.checklist.filter((_, i) => i !== index));
    },
    [milestone.checklist, updateChecklist],
  );

  return (
    <Box
      data-testid={`milestone-checklist-${milestoneId}`}
      sx={{ p: 1.75, backgroundColor: colors.white, borderBottom: `1px solid ${colors.lightGray}` }}
    >
      {inEditMode && (
        <TextField
          type="date"
          label={t('projects.milestonesTab.targetDate')}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
          value={
            milestone.targetDate ? milestone.targetDate.toDate().toISOString().split('T')[0] : ''
          }
          onChange={(e) =>
            void updateMilestoneTargetDate(projectId, milestoneId, e.target.value).then(onDone)
          }
          data-testid={`milestone-target-date-${milestoneId}`}
          sx={{ mb: 1.5 }}
        />
      )}

      <Stack spacing={1}>
        {milestone.checklist.map((item, idx) => (
          <ChecklistCard
            key={idx}
            item={item}
            index={idx}
            canEdit={inEditMode}
            userOptions={userOptions}
            onUpdate={handleUpdateItem}
            onDelete={handleDeleteItem}
          />
        ))}
      </Stack>

      {inEditMode && (
        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={() => void handleAddItem()}
          data-testid={`milestone-add-item-${milestoneId}`}
          sx={{ mt: 1 }}
        >
          {t('projects.milestonesTab.addItem')}
        </Button>
      )}
    </Box>
  );
}

// ─── Checklist Card ─────────────────────────────────────────────

interface ChecklistCardProps {
  item: ChecklistItem;
  index: number;
  canEdit: boolean;
  userOptions: string[];
  onUpdate: (index: number, field: keyof ChecklistItem, value: unknown) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
}

const STATUS_CHIP_COLOR: Record<ChecklistStatus, 'default' | 'success'> = {
  pending: 'default',
  done: 'success',
  na: 'default',
};

function initials(displayName: string): string {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function ChecklistCard({ item, index, canEdit, userOptions, onUpdate, onDelete }: ChecklistCardProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Partial<ChecklistItem>>({});

  const commit = (field: keyof ChecklistItem, value: unknown) => {
    void onUpdate(index, field, value);
  };

  const statusLabel = t(
    `projects.milestonesTab.status${item.status.charAt(0).toUpperCase()}${item.status.slice(1)}`,
  );
  const dateLabel = item.date ? item.date.toDate().toLocaleDateString() : null;

  if (!canEdit) {
    return (
      <Card
        data-testid={`checklist-row-${index}`}
        sx={{
          transition: 'background-color 120ms',
          '&:hover': { bgcolor: colors.peach },
        }}
      >
        <CardContent sx={{ '&:last-child': { pb: 2 }, py: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', mb: 1 }}>
            <Chip
              label={statusLabel}
              size="small"
              color={STATUS_CHIP_COLOR[item.status]}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, flexGrow: 1, wordBreak: 'break-word' }}
            >
              {item.what || <em style={{ color: colors.textMuted }}>{t('projects.milestonesTab.untitled')}</em>}
            </Typography>
            {dateLabel && (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: colors.textMuted }}>
                <CalendarTodayIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption">{dateLabel}</Typography>
              </Stack>
            )}
          </Stack>
          {item.who && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: item.comment ? 1 : 0 }}>
              <Avatar
                sx={{
                  width: 24, height: 24, fontSize: 11, fontWeight: 700,
                  bgcolor: colors.lightGray, color: colors.bodyText,
                }}
              >
                {initials(item.who)}
              </Avatar>
              <Typography variant="body2" color="text.secondary">
                {item.who}
              </Typography>
            </Stack>
          )}
          {item.comment && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', mt: 1 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: colors.textMuted, mt: '2px' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {item.comment}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-testid={`checklist-row-${index}`}
      sx={{ transition: 'background-color 120ms', '&:hover': { bgcolor: colors.peach } }}
    >
      <CardContent sx={{ '&:last-child': { pb: 1.5 }, py: 1.5, px: 1.5 }}>
        {/* Row 1: Status + Date + Delete — wraps on narrow cards */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Select
            size="small"
            value={item.status}
            onChange={(e) => commit('status', e.target.value as ChecklistStatus)}
            data-testid={`checklist-status-${index}`}
            sx={{ minWidth: 110, flexShrink: 0 }}
          >
            <MenuItem value="pending">{t('projects.milestonesTab.statusPending')}</MenuItem>
            <MenuItem value="done">{t('projects.milestonesTab.statusDone')}</MenuItem>
            <MenuItem value="na">{t('projects.milestonesTab.statusNa')}</MenuItem>
          </Select>
          <TextField
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={item.date ? item.date.toDate().toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const ts = e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null;
              commit('date', ts);
            }}
            data-testid={`checklist-date-${index}`}
            sx={{ minWidth: 135, flexGrow: 1 }}
          />
          <Tooltip title={t('projects.milestonesTab.deleteItem')}>
            <IconButton
              size="small"
              onClick={() => void onDelete(index)}
              data-testid={`checklist-delete-${index}`}
              aria-label={t('projects.milestonesTab.deleteItem')}
              sx={{ flexShrink: 0 }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Row 2: What — the main title/description */}
        <TextField
          fullWidth
          size="small"
          label={t('projects.milestonesTab.what')}
          value={editing.what ?? item.what}
          onChange={(e) => setEditing((p) => ({ ...p, what: e.target.value }))}
          onBlur={() => {
            if (editing.what !== undefined && editing.what !== item.what) {
              commit('what', editing.what);
            }
            setEditing((p) => ({ ...p, what: undefined }));
          }}
          data-testid={`checklist-what-${index}`}
          sx={{ mb: 1.5 }}
        />

        {/* Row 3: Who — autocomplete with person icon */}
        <Autocomplete
          freeSolo
          size="small"
          options={userOptions}
          value={editing.who ?? item.who}
          onChange={(_, value) => {
            const next = value ?? '';
            if (next !== item.who) commit('who', next);
            setEditing((p) => ({ ...p, who: undefined }));
          }}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') setEditing((p) => ({ ...p, who: value }));
          }}
          onBlur={() => {
            if (editing.who !== undefined && editing.who !== item.who) {
              commit('who', editing.who);
            }
            setEditing((p) => ({ ...p, who: undefined }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('projects.milestonesTab.who')}
              size="small"
              data-testid={`checklist-who-${index}`}
            />
          )}
          sx={{ mb: 1.5 }}
        />

        {/* Row 4: Comment — optional notes */}
        <TextField
          fullWidth
          size="small"
          label={t('projects.milestonesTab.comment')}
          value={editing.comment ?? item.comment ?? ''}
          onChange={(e) => setEditing((p) => ({ ...p, comment: e.target.value }))}
          onBlur={() => {
            if (editing.comment !== undefined && editing.comment !== (item.comment ?? '')) {
              commit('comment', editing.comment);
            }
            setEditing((p) => ({ ...p, comment: undefined }));
          }}
          data-testid={`checklist-comment-${index}`}
          multiline
          maxRows={3}
        />
      </CardContent>
    </Card>
  );
}
